import { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { BaseContext } from "../../../shared/base-context";
import { useAuthToken } from "../../../shared/hooks/useAuthToken";
import { useConvexSiteUrl } from "../../../shared/hooks/useConvexSiteUrl";
import { Assistant, ChatMessage, MainRoutes, MultipleChoiceQuestionResponse, SystemMessages } from "../../../shared/models";
import { useThreadMessages } from "./useThreadMessages";
import { useGetRun } from "./useGetRun";
import { useMessageRenderer } from "./useMessageRenderer";
import { ChatInput } from "../components/chat-input";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../../convex/_generated/api";
import { useMutation } from "convex/react";
import { Id } from "../../../convex/_generated/dataModel";
import { useStableQuery } from "../../../shared/hooks/useStableQuery";
import { useFetchSessionById } from "./useFetchSessionById";
export const useChat = () => {
  const [searchParams] = useSearchParams();
  const token = useAuthToken();
  const { convexUserId, debug, user } = useContext(BaseContext);

  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [responseInProgress, setResponseInProgress] = useState(false);
  const [showInput, setShowInput] = useState(true);
  const location = useLocation();

  const completeSession = useMutation(api.sessions.completeSession);

  const [readonly, setReadonly] = useState(false);
  const [testing, setTesting] = useState(false);
  const [userName, setUserName] = useState("");
  const [sessionId, setSessionId] = useState('');
  const personalAssistant = useStableQuery(api.user_personal_assistant.getByUserId, convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip');
  const session = useFetchSessionById(sessionId);
  const assistant = useStableQuery(api.assistants.getById, session?.assistant_id ? { id: session?.assistant_id as Id<"assistants"> } : "skip") || undefined;
  const userMetadata = useStableQuery(api.user_metadata.getUserMetadata, convexUserId ? { user_id: convexUserId as Id<"users"> } : 'skip');
  const clearNextQuestion = useMutation(api.user_personal_assistant.clearNextQuestionPublic);
  const sendMessageMutation = useMutation(api.sessions.sendMessage);

  // Auto-question timer refs
  const autoQuestionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const userTypingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // parse url query params
  const navigate = useNavigate();
  
  useEffect(() => {
    const id = searchParams.get("sessionId");
    const name = searchParams.get("name");
    const testing = searchParams.get("testing");
    setTesting(testing === "true");

    if (id) {
      setSessionId(id);
    }

    if (name) {
      setUserName(name);
    }
  }, [searchParams, location]);

  // set the user name (skip when viewing another user's session via query param)
  useEffect(() => {
    if (user && !searchParams.get("name")) {
      setUserName(userMetadata?.nickname || userMetadata?.legal_name?.split(' ')[0] || user.given_name || '');
    }
  }, [user, userMetadata, searchParams]);

  // determine if the chat is readonly
  useEffect(() => {

    setReadonly(session?.isComplete ?? false);
    setResponseInProgress(session?.response_in_progress ?? false);
  }, [session]);

  // determine if the chat input should be shown
  useEffect(() => {
    if (!assistant && !personalAssistant) {
      setShowInput(false);
      return;
    }
    // if (!!messages.find(x => x.completed)) {
    //   setShowInput(false);
    //   return;
    // }
    let showInput = !readonly && !!convexUserId && !session?.isComplete;
    // if (assistant?.multiple_choice) {
    //   showInput = false
    // }

    // if (assistant?.show_upload_resume_button) {
    //   if (!userMetadata) {
    //     showInput = false;
    //   } else if (!userMetadata.resume_summary && userMetadata.no_resume === null) {
    //     showInput = false;
    //   } else if ((userMetadata.no_resume === true || userMetadata.resume_summary) && !session?.isComplete) {
    //     showInput = true;
    //   }
    // }
    setShowInput(showInput);
  }, [session, readonly, convexUserId, assistant]);

  // Track the last message to detect updates for scrolling
  const lastMessageRef = useRef<string>("");
  const messagesLengthRef = useRef<number>(0);
  const userScrolledUpRef = useRef<boolean>(false);
  const isAutoScrollingRef = useRef<boolean>(false);

  // Detect user-initiated scroll events
  useEffect(() => {
    const handleScroll = () => {
      // If we're currently auto-scrolling, don't mark as user scroll
      if (isAutoScrollingRef.current) {
        return;
      }

      // Check if user scrolled up (not at bottom)
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - scrollPosition;

      // If user scrolled up (more than 200px from bottom), set flag and stop auto-scroll
      if (distanceFromBottom > 200) {
        userScrolledUpRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // scroll to the bottom when a new message starts streaming
  useEffect(() => {
    if (!isLoading && session?.messages && session?.messages.length > 0) {
      const lastMessage = session.messages[session.messages.length - 1];
      const lastMessageContent = lastMessage.content || lastMessage.body || "";
      const currentMessagesLength = session.messages.length;

      // Check if a new message was added or content was updated
      const isNewMessage = currentMessagesLength !== messagesLengthRef.current;
      const isContentUpdated = lastMessageContent !== lastMessageRef.current;

      // Reset "user scrolled up" flag when a new message starts streaming
      if (isNewMessage) {
        userScrolledUpRef.current = false;
      }

      // Auto-scroll if it's a new message OR if content is being updated during streaming
      if ((isNewMessage || isContentUpdated) && !userScrolledUpRef.current) {
        lastMessageRef.current = lastMessageContent;
        messagesLengthRef.current = currentMessagesLength;

        // Auto-scroll to bottom - use requestAnimationFrame to ensure DOM is updated
        isAutoScrollingRef.current = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Scroll to the actual bottom of the document
            const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
            window.scrollTo({ top: scrollHeight, behavior: "smooth" });
            setTimeout(() => {
              isAutoScrollingRef.current = false;
            }, 500);
          });
        });
      } else if (isNewMessage || isContentUpdated) {
        // Update refs even if we're not scrolling
        lastMessageRef.current = lastMessageContent;
        messagesLengthRef.current = currentMessagesLength;
      }
    } else if (session?.messages?.length === 0) {
      // Reset refs when messages are cleared
      lastMessageRef.current = "";
      messagesLengthRef.current = 0;
      userScrolledUpRef.current = false;
    }
  }, [session?.messages, isLoading]);

  const handleSessionComplete = () => {
    completeSession({ sessionId: session?._id as Id<"sessions"> });
  }

  const sendMessage = async (message?: string) => {
    if (!message || !user?.firstName || !sessionId || responseInProgress) {
      return;
    }

    sendMessageMutation({ session_id: sessionId, message: message });
    return;
  };

  const renderChatInput = useCallback(() => {
    return <ChatInput
        onMessageSubmit={sendMessage}
        responseInProgress={responseInProgress}
        isComplete={session?.isComplete}
        sessionId={session?._id as Id<"sessions">}
        assistantId={assistant?._id as Id<"assistants">}
        testing={testing}
        showInput={showInput}
      />
  }, [sendMessage, responseInProgress, session?.isComplete]);

  const messageRendererProps = useMemo(() => ({
    messages: session?.messages || [],
    userName,
    readonly,
    responseInProgress,
    debug,
    sendMessage,
    handleSessionComplete,
    assistant: assistant as Assistant | undefined
  }), [session?.messages, userName, readonly, responseInProgress, debug, sendMessage, handleSessionComplete, assistant]);

  const { renderMessages } = useMessageRenderer(messageRendererProps);

  return {
    isLoading,
    renderMessages,
    renderChatInput
  };
};